import React, { useRef, useState } from 'react';
import { BooleanInput, SelectInput, useNotify, useRefresh, useRecordContext } from 'react-admin';
import { Avatar, Box, Button, Grid, Typography } from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import { useWatch } from 'react-hook-form';
import { resolveUploadUrl } from '../utils/resolveUploadUrl';
import { UserRole } from '../types/types';

const MAX_LOGO_SIZE_BYTES = 5 * 1024 * 1024;

interface SellerAdminFieldsProps {
    userId?: string;
    /** Product admins: seller type only (no logo / premium). */
    sellerTypeOnly?: boolean;
}

const sellerTypeInput = (
    <Grid item xs={12} sm={6}>
        <SelectInput
            source="seller_category"
            label="Seller type"
            helperText="Seller, reseller, marketplace (polymer market), or recycle (reprocess / market-first)."
            choices={[
                { id: 'seller', name: 'Seller' },
                { id: 'reseller', name: 'Reseller' },
                { id: 'marketplace', name: 'Marketplace' },
                { id: 'recycle', name: 'Recycle' },
            ]}
            optionText="name"
            optionValue="id"
            fullWidth
        />
    </Grid>
);

export const SellerAdminFields: React.FC<SellerAdminFieldsProps> = ({
    userId,
    sellerTypeOnly = false,
}) => {
    const record = useRecordContext();
    const watchedRoleId = useWatch({ name: 'role_id' });
    const roleId = watchedRoleId ?? record?.role_id;
    const isSellerRole = Number(roleId) === UserRole.Seller;
    const notify = useNotify();
    const refresh = useRefresh();
    const fileRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState<string | null | undefined>(record?.avatar_url);

    if (!isSellerRole) {
        return null;
    }

    const handleUpload = async (file: File) => {
        if (!userId || !file) {
            return;
        }
        if (file.size > MAX_LOGO_SIZE_BYTES) {
            notify('Logo must be 5 MB or smaller', { type: 'warning' });
            return;
        }

        setUploading(true);
        try {
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const token = localStorage.getItem('user_authToken');
            const formData = new FormData();
            formData.append('logo', file);
            const res = await fetch(`${baseUrl}/users/seller/${userId}/logo`, {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: formData,
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Upload failed');
            }
            const data = await res.json();
            setAvatarUrl(data.avatar_url);
            notify('Company logo updated', { type: 'success' });
            refresh();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to upload logo';
            notify(message, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const preview = resolveUploadUrl(avatarUrl || record?.avatar_url);

    if (sellerTypeOnly) {
        return sellerTypeInput;
    }

    return (
        <>
            {userId && (
                <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Company logo
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Avatar
                            src={preview || undefined}
                            alt="Company logo"
                            sx={{ width: 64, height: 64, bgcolor: 'grey.200' }}
                        />
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(event) => {
                                const file = event.target.files?.[0];
                                if (file) {
                                    handleUpload(file);
                                }
                                event.target.value = '';
                            }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<PhotoCameraOutlinedIcon />}
                            disabled={uploading}
                            onClick={() => fileRef.current?.click()}
                        >
                            {uploading ? 'Uploading…' : 'Upload logo'}
                        </Button>
                    </Box>
                </Grid>
            )}
            {sellerTypeInput}
            <Grid item xs={12}>
                <BooleanInput
                    source="premium_seller"
                    label="Premium reprocessor (featured in Reprocess By Seller marquee)"
                />
            </Grid>
        </>
    );
};

export default SellerAdminFields;
